/*
	This just renders a basic table of Creatives with their name, ID, and formatted approval status.

	It takes an array, `creatives` and an optional `shouldLinkPreview` prop, which defaults to true.

	If `shouldLinkPreview` is true, the Creative name will link to a new tab to the appropriate
	Creative Preview app, depending on the adType.

	URL-encoded characters in the displayed Creative name are always decoded.
*/

import React from "react";
import PropTypes from "prop-types";
import { useFlag } from '@innovid/feature-flags-react';
import { TextOverflow, creativePreviewURL, safeDecodeURIComponent } from "utils/common";
import { FEATURE_FLAGS } from 'featureFlag/featureFlag';
import StatusColumn from 'widgets/grid/cells/StatusColumn';

import "./CreativesTable.css";

export default function CreativesTable(props){
	const { creatives, shouldLinkPreview, className, shouldShowStatus, shouldShowComposition } = props;
	const forceNewPreview = useFlag(FEATURE_FLAGS.NEW_PREVIEW_LINKS);
	const rows = creatives.map((creative) => {
		const { name, id, approval } = creative || {};
		let nameContent = <TextOverflow value={safeDecodeURIComponent(name)} />;
		if(shouldLinkPreview){
			nameContent = <a target="_blank" href={creativePreviewURL(creative, forceNewPreview)}>{nameContent}</a>;
		}

		return (
			<tr key={id}>
				<td className="creative-name-column">
					{nameContent}
				</td>
				{ shouldShowComposition && <td className="creative-composition-name-column">{creative.compositionName}</td> }
				<td className="creative-id-column">{id}</td>
				{ shouldShowStatus && <td className="creative-status-column"><StatusColumn value={approval} shouldShowLinks={false} /></td> }
			</tr>
		);
	});

	return (
		<table className={`creatives-table basic-table ${className}`}>
			<thead>
				<tr>
					<th className="creative-name-column">Creative Name</th>
					{ shouldShowComposition && <th className="creative-composition-name-column">Composition</th> }
					<th className="creative-id-column">ID</th>
					{ shouldShowStatus && <th className="creative-status-column">Status</th> }
				</tr>
			</thead>
			<tbody>
				{rows}
			</tbody>
		</table>
	);
}

CreativesTable.displayName = "CreativesTable";

CreativesTable.propTypes = {
	creatives: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.number,
		]),
		name: PropTypes.string,
		adType: PropTypes.string, // We need the adType to know what Preview URL to use
	})),
	shouldLinkPreview: PropTypes.bool,
	shouldShowStatus: PropTypes.bool,
	shouldShowComposition: PropTypes.bool,
	className: PropTypes.string,
};

CreativesTable.defaultProps = {
	creatives: [],
	shouldLinkPreview: true,
	shouldShowStatus: true,
	shouldShowComposition: false,
	className: "",
};
